import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_WEB_ACCESS_EXCEPTIONS_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzContentControlWebAccessExceptionsGridDataProvider } from './GzContentControlWebAccessExceptionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates, validatePermission } from './utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';
import { ContentControlWebAccessRule } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { sanitizeExclusionItemFormValue } from './utils/converters';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { GzDialog } from '../../../../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../../../../ui/gzDialog';
import { selectPoliciesState } from '../../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import {
  ImportFileApiClients,
  NetworkProtectionApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzPolicyUiSettingsImportDialog } from '../../../../../../../common/policyPanel/uiSettings/dialogs/import/GzPolicyUiSettingsImportDialog';
import { getPhantom } from '../../../../../redux/actions';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_WEB_ACCESS_EXCEPTIONS_GRID)
export class GzContentControlWebAccessExceptionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlWebAccessRule,
  ContentControlWebAccessRule,
  GzContentControlWebAccessExceptionsGridDataProvider
> {
  protected clients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'NetworkProtection'
  ) as NetworkProtectionApiClients;

  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected translations = {
    editRow: translateFn('Edit exclusion'),
    deleteRow: translateFn('Delete'),
    emptyState: translateFn('No exclusions'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#export-csv-button')
  protected exportButton: GzButtonWebComponent;

  protected getDataProvider(): GzContentControlWebAccessExceptionsGridDataProvider {
    return new GzContentControlWebAccessExceptionsGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-tooltip
        id="tooltip"
        class="tooltip"
        tooltipText="${translateFn('Export exclusions (available only for saved policies)')}"
      >
        <gz-button
          id="export-csv-button"
          type="${ButtonTypes.OUTLINE}"
          @click="${this.exportExclusions}"
          ?disabled="${this.isExportButtonDisabled()}"
          >${translateFn('Export')}</gz-button
        >
      </gz-tooltip>
      <gz-button id="import-csv-button" type="${ButtonTypes.OUTLINE}" @click="${this.importExclusions}"
        >${translateFn('Import')}</gz-button
      >
      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete exclusions?'),
          translateFn('You are about to delete the selected exclusions. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-content-control-web-access-exceptions-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-content-control-traffic-exclusion-item>`;
  }

  public setExportButtonDisabledState() {
    this.exportButton.disabled = this.isExportButtonDisabled();
  }

  private isExportButtonDisabled() {
    const state = selectPoliciesState(store.getState());

    // is disabled for clone or add new policy
    return getPhantom(state);
  }

  private exportExclusions() {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `
      <div>
        <p>${translateFn('Exclusions will be saved locally in CSV format.')}</p>
        <gz-info-box id="export-warning" severity="warning" style="padding-left: 0px; --info-box-width: 100%;">
          ${translateFn('Make sure to save the policy to export the latest exclusions.')}
        </gz-info-box>
      </div>
    `;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="full" color="primary">${translateFn('Export')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
    `;

    const footerBtnOne = footer.querySelector('#buttonOne');
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: translateFn('Export exclusions'),
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        this.clients.ExportWebAccessRules(this.policyId);
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
  }

  private importExclusions() {
    const importFn = async (file: File) => {
      const csvFields = '{"webAddress":"string","permission":"int"}';
      const importClient = PolicyDependenciesContainer.getInstance().resolveDependency(
        'ImportFile'
      ) as ImportFileApiClients;

      return await importClient.ImportCsv(csvFields, file);
    };

    const dialog = new GzPolicyUiSettingsImportDialog(importFn, this.appendExclusionsItemsToGrid.bind(this));
    dialog.show();
  }

  private appendExclusionsItemsToGrid(exclusionItems: ContentControlWebAccessRule[]): {
    valid: boolean;
    rowWithErrors: number[];
  } {
    this.gridComponent.propertiesSet = false;

    const exclusionItemsToAppendLength = exclusionItems.length;
    const rowWithErrors: number[] = [];
    let exclusionItem: ContentControlWebAccessRule;

    for (let i = 0; i < exclusionItemsToAppendLength; i++) {
      exclusionItem = exclusionItems[i];

      // validate empty fields
      if (!exclusionItem || !exclusionItem.webAddress || !exclusionItem.permission) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // validate type
      if (!validatePermission(exclusionItem.permission).valid) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // sanitize exclusion item
      exclusionItem = sanitizeExclusionItemFormValue(exclusionItem);

      // check for duplicates - error is skipped
      if (
        !validateDuplicates(
          exclusionItem,
          (this.gridComponent.dataProvider as GzContentControlWebAccessExceptionsGridDataProvider).items
        ).valid
      ) {
        continue;
      }

      (this.gridComponent.dataProvider as GzContentControlWebAccessExceptionsGridDataProvider).addItem(exclusionItem);
    }
    this.gridComponent.propertiesSet = true;
    this.onGridItemsChange();

    return {
      valid: !rowWithErrors.length,
      rowWithErrors: rowWithErrors,
    };
  }

  protected sanitizeGridItemBeforeAdd(gridItemFormValue: ContentControlWebAccessRule): ContentControlWebAccessRule {
    return sanitizeExclusionItemFormValue(gridItemFormValue);
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ContentControlWebAccessRule): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzContentControlWebAccessExceptionsGridDataProvider).items
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.WEB_ADDRESS, [
      RequiredValidator(translateFn('Required field')),
    ]);
    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.PERMISSION, [
      RequiredValidator(translateFn('Required field')),
    ]);
  }

  protected isEditedRowChanged(oldItem: ContentControlWebAccessRule, newItem: ContentControlWebAccessRule): boolean {
    return oldItem.webAddress !== newItem.webAddress || oldItem.permission !== newItem.permission;
  }

  protected sanitizeGridItemBeforeEdit(gridItemFormValue: ContentControlWebAccessRule): ContentControlWebAccessRule {
    return sanitizeExclusionItemFormValue(gridItemFormValue);
  }

  protected validateGridItemBeforeEdit(
    oldItem: ContentControlWebAccessRule,
    newItem: ContentControlWebAccessRule
  ): ValidationResult {
    // skip if entity is not changed
    if (newItem.webAddress === oldItem.webAddress) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected revertGridCellEditValue(
    gridCellEdit: GridCellEditWebComponent,
    oldItem: ContentControlWebAccessRule
  ): void {
    if (gridCellEdit.id === ColumnKeys.WEB_ADDRESS) {
      gridCellEdit.value = oldItem.webAddress;
    }

    if (gridCellEdit.id === ColumnKeys.PERMISSION) {
      gridCellEdit.value = oldItem.permission;
    }
  }
}
